import { useCallback, useEffect, useState } from "react";

/**
 * A deliberately small router.
 *
 * The dashboard has a handful of screens. TanStack Router earns its keep on
 * nested layouts, loaders and typed search params; here it would be more moving
 * parts than routes. Recorded as a deviation from the plan's stack pin: the
 * admin console can revisit if its routing grows.
 *
 * Every path below is served the SPA shell by the platform's not-found handler,
 * which is what makes `/invitations/<token>` a link somebody can be emailed
 * rather than a 404 followed by a redirect.
 */

export type Route =
  | { readonly name: "sites" }
  | { readonly name: "site"; readonly id: string; readonly tab: string }
  | { readonly name: "groups" }
  | { readonly name: "members" }
  | { readonly name: "billing" }
  | { readonly name: "audit" }
  | { readonly name: "invitation"; readonly token: string }
  | { readonly name: "help" }
  | { readonly name: "settings" };

/**
 * The invitation token, as `issueToken` mints it: base64url, 43 characters.
 * The bounds match the platform's `acceptParams` schema, so an obviously
 * malformed link renders the sites screen rather than an acceptance screen
 * certain to fail. The check that matters is the conditional UPDATE
 * server-side; this one only decides which screen to draw.
 */
const INVITATION_PATH = /^\/invitations\/([A-Za-z0-9_-]{20,200})\/?$/;

export function parseRoute(pathname: string): Route {
  if (pathname === "/help") return { name: "help" };
  if (pathname === "/settings") return { name: "settings" };
  if (pathname === "/groups") return { name: "groups" };
  if (pathname === "/members") return { name: "members" };
  if (pathname === "/billing") return { name: "billing" };
  /**
   * Reachable by URL whatever the plan says, and deliberately so (LGN-146).
   * The rail hides the item when the entitlement is off, but a bookmark from
   * before a plan lapsed still has to land somewhere that explains itself; the
   * screen refuses with a sentence rather than the router pretending the path
   * does not exist and dropping the reader on the sites list.
   */
  if (pathname === "/audit") return { name: "audit" };

  const invitation = INVITATION_PATH.exec(pathname);
  if (invitation?.[1]) return { name: "invitation", token: invitation[1] };

  // The tab is part of the URL so a link to someone's activity log is a link,
  // and a refresh does not silently return them to the first tab.
  const match = /^\/sites\/([0-9a-f-]{36})(?:\/([a-z]+))?$/i.exec(pathname);
  if (match?.[1]) return { name: "site", id: match[1], tab: match[2] ?? "deploy" };
  return { name: "sites" };
}

export function useRouter(): {
  route: Route;
  navigate: (to: string) => void;
} {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.pathname));

  useEffect(() => {
    const onPop = (): void => setRoute(parseRoute(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback((to: string) => {
    window.history.pushState({}, "", to);
    setRoute(parseRoute(to));
    window.scrollTo(0, 0);
  }, []);

  return { route, navigate };
}

/**
 * Route a click on an internal anchor that nothing else has handled.
 *
 * The shell needs this because `AppLayout` renders its own navigation: the
 * items are real `<a href>` elements built by the design library, and the
 * library gives no per-item click hook to hand a client-side router. Rather
 * than fork the component, the shell listens on its root in the BUBBLE phase
 * and finishes the job the anchor started.
 *
 * The bubble phase is what makes the two mechanisms safe together. {@link Link}
 * calls `preventDefault` on the way past, so by the time this runs the event is
 * already marked handled and it does nothing; an anchor the library rendered
 * arrives unmarked and is routed here. Capture-phase interception would run
 * first and push the same entry twice.
 *
 * Everything a real link can do is left alone: modifier and middle clicks, any
 * anchor with a `target` or a `download`, and every href that is not a path on
 * this origin.
 */
export function routeInternalLink(
  event: React.MouseEvent<HTMLElement>,
  navigate: (to: string) => void,
): void {
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const target = event.target;
  if (!(target instanceof Element)) return;
  const anchor = target.closest("a");
  if (anchor === null) return;
  if (anchor.target !== "" || anchor.hasAttribute("download")) return;

  // The attribute, not the resolved `href` property: a same-origin absolute
  // URL is somebody deliberately leaving, and `//evil.example` is not a path.
  const to = anchor.getAttribute("href");
  if (to === null || !to.startsWith("/") || to.startsWith("//")) return;

  event.preventDefault();
  navigate(to);
}

/** An anchor that routes without a full page load, but is still a real link. */
export function Link(props: {
  to: string;
  navigate: (to: string) => void;
  children: React.ReactNode;
  className?: string;
}): React.ReactElement {
  return (
    <a
      href={props.to}
      className={props.className}
      onClick={(event) => {
        // Let modifier-clicks and middle-clicks behave normally — a link the
        // browser cannot open in a new tab is not really a link.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        props.navigate(props.to);
      }}
    >
      {props.children}
    </a>
  );
}
